'use client';

import { useClientConfig, type Currency } from '@shared/utils';
import type { LotteryName } from '../../../lottery-purchases/types';
import type { NextDrawItem } from '../../../lottery-results/components/NextDraw/types';
import { useLotteryCategory } from '../../hooks/useLotteryCategory/useLotteryCategory';
import { LotteryCategoryLayout, type LotteryCategoryLayoutContainerProps } from '../../LotteryCategoryLayout/LotteryCategoryLayout';
import type { LotteriesCategory } from '../../types';
import { filterHugeJackpots, filterOneOrLess } from '../utils/filters';
import { sortAlphabetically, sortByDrawingDateDesc } from '../utils/sort';

type StaticCategory = Exclude<LotteriesCategory, 'todays_draws'>;

type LotteryCategoryPageContentProps = LotteryCategoryLayoutContainerProps & {
  category: StaticCategory;
  lotteries: LotteryName[];
  skeletonItems?: number;
  emptyMessage?: React.ReactNode;
};

const STATIC_TRANSFORMS: Record<StaticCategory, (data: NextDrawItem[], currency: Currency) => NextDrawItem[]> = {
  all_lottos: sortAlphabetically,
  best_odds: sortByDrawingDateDesc,
  most_popular: sortByDrawingDateDesc,
  quick_draws: sortByDrawingDateDesc,
  huge_jackpots: filterHugeJackpots,
  one_or_less: filterOneOrLess,
};

/**
 * Shared client boundary for the lottery category pages whose filter/sort logic is a pure
 * function of the fetched data (all, best-odds, huge-jackpots, most-popular, one-or-less,
 * quick-draws). It owns data fetching and dispatches to the right transform by category, so
 * the page.tsx files themselves can stay plain (non-client) components.
 */
function LotteryCategoryPageContent({ category, lotteries, skeletonItems, emptyMessage, ...props }: LotteryCategoryPageContentProps) {
  const { data, isLoading } = useLotteryCategory(category, lotteries);
  const { currency } = useClientConfig();
  const lotteriesData = STATIC_TRANSFORMS[category](data, currency);

  return <LotteryCategoryLayout category={category} isLoading={isLoading} lotteriesData={lotteriesData} skeletonItems={skeletonItems} emptyMessage={emptyMessage} {...props} />;
}

export { LotteryCategoryPageContent };
